import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid, TextField, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject, updateProjectAchievement, deleteProjectAchievement } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProjectAchievementEditPage() {
  const { businessDomainId, businessUnitId, projectId, goalId, achievementId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}`;

  const [goal, setGoal] = useState(null);
  const [originalAchievement, setOriginalAchievement] = useState(null);
  const [form, setForm] = useState({ actualValue: "", actualDate: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await fetchProject(projectId);
        // goalId は配列インデックス
        const foundGoal = (d.goals || [])[Number(goalId)];
        setGoal(foundGoal || null);
        if (foundGoal) {
          // 実績はプロジェクトレベルの achievements から対象指標名でフィルタリング
          const filtered = (d.achievements || []).filter(
            (a) => a.goal?.indicator?.indicatorName === foundGoal?.indicator?.indicatorName
          );
          // achievementId は filtered の配列インデックス
          const foundAch = filtered[Number(achievementId)];
          setOriginalAchievement(foundAch || null);
          if (foundAch) {
            setForm({
              actualValue: foundAch.actualValue ?? "",
              actualDate: foundAch.actualDate ? foundAch.actualDate.substring(0, 10) : "",
            });
          }
        }
      } catch (e) { notify.apiError(e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, goalId, achievementId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedAchievement = {
      ...originalAchievement,
      actualValue: form.actualValue,
      actualDate: form.actualDate ? `${form.actualDate}T00:00:00.000Z` : null,
      updatedAchievementAt: new Date().toISOString(),
    };
    try {
      // 旧アプリ同様 { previous, post } 形式で送信
      await updateProjectAchievement(projectId, { previous: originalAchievement, post: updatedAchievement });
      notify.success(t("message.update.success"));
      navigate(`${basePath}/achievements`);
    } catch (err) { notify.apiError(err); }
  };

  const handleDelete = async () => {
    try {
      await deleteProjectAchievement(projectId, originalAchievement);
      notify.success(t("message.delete.success"));
      navigate(`${basePath}/achievements`);
    } catch (err) { notify.apiError(err); }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;
  }

  if (!goal || !originalAchievement) {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>{t("message.notFound")}</Typography>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}/achievements`)}>{t("common.back")}</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.achievement.edit.title")}</Typography>
        <Button variant="contained" color="error" onClick={() => setDeleteOpen(true)}>
          {t("common.delete")}
        </Button>
      </Box>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("project.goal.indicator")} value={goal.indicator?.indicatorName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.unit")} value={goal.indicator?.unit} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.measuringMethod")} value={goal.indicator?.measuringMethod} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.evaluationStandard")} value={goal.indicator?.evaluationStandard} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.description")} value={goal.indicator?.description} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.plannedValue")} value={`${goal.plannedValue ?? ""}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}`} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.plannedDate")} value={formatDate(goal.plannedDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.deadline")} value={formatDate(goal.deadline)} />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={`${t("project.achievement.actualValue")}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}`}
              name="actualValue"
              value={form.actualValue}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required type="date" label={t("project.achievement.actualDate")} name="actualDate"
              value={form.actualDate} onChange={handleChange} slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}/achievements`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
